import { inject, Injectable } from '@angular/core';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { Observable } from 'rxjs';

import { UserStore } from '../store/user.store';
import { EnvironmentService } from '../../environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private oauthService = inject(OAuthService);
  private userStore = inject(UserStore);
  private readonly environmentService = inject(EnvironmentService);

  public async init(): Promise<void> {
    const authConfig = this.environmentService.getAuthConfig();
    // clearHashAfterLogin: false prevents the library from running a regex over
    // the query string that incorrectly strips "code=…" from "workflow_code=…".
    this.oauthService.configure({ ...authConfig, clearHashAfterLogin: false });
    try {
      // preventClearHashAfterLogin: true skips the regex cleanup in tryLoginCodeFlow
      // that incorrectly strips "code=…" from query params like "workflow_code=DMG0001".
      await this.oauthService.loadDiscoveryDocumentAndTryLogin({ preventClearHashAfterLogin: true });
      this.oauthService.setupAutomaticSilentRefresh();
      if (this.isAuthenticated()) {
        this.userStore.loadUserInfo();
      }
    } catch (e) {
      console.error('Load discover document error', e);
    } finally {
      // Remove only the OAuth params so they don't persist in history, even when login fails.
      const url = new URL(window.location.href);
      ['code', 'state', 'session_state', 'iss'].forEach(p => url.searchParams.delete(p));
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }

  public login() {
    this.oauthService.initCodeFlow();
  }

  public logout() {
    this.oauthService.logOut();
  }

  public getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  public isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  public getAuthEventsStream(): Observable<OAuthEvent> {
    return this.oauthService.events;
  }
}
